import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  ArrowLeft, MonitorPlay, Loader2, MessageSquare, /* Info, */ Layers,
  ChevronRight, ChevronLeft, Play, Pause, SkipBack, Download,
} from "lucide-react";
import DicomViewer from "@/components/DicomViewer";
import VideoPlayer from "@/components/VideoPlayer";
import PdfViewer from "@/components/PdfViewer";
import DocumentDownload from "@/components/DocumentDownload";
import OfficeDocumentViewerModal from "@/components/OfficeDocumentViewerModal";
import StudyDiscussion from "@/components/StudyDiscussion";
import { useModalities } from "@/contexts/ModalitiesContext";
import { useRbac } from "@/hooks/useRbac";
import { isMissingPatientValue } from "@/lib/patientDisplay";
import { isOfficeDocumentFile, isPdfFile } from "@/lib/modalities";
import {
  captureDwvLayerThumb,
  getCachedDicomFilePreviewThumb,
  getDicomFilePreviewThumb,
  rememberDicomFilePreviewThumb,
  setDicomPreviewPaused,
} from "@/lib/dicomFilePreviewThumb";
import { shareFileServeUrl } from "@/lib/shareApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Prefetched fluoro slice-0 DICOM blobs (fileId -> blobUrl). Shared by sidebar + viewer. */
const fluoroRawCache = new Map();
const fluoroRawLoading = new Map();
/** Background prefetch queue — never starve the active viewer download. */
const fluoroRawBgQueue = [];
let fluoroRawBgActive = 0;
const FLUORO_RAW_BG_MAX = 1;

const isFluoroRawUrl = (url) => {
  for (const cached of fluoroRawCache.values()) {
    if (cached === url) return true;
  }
  return false;
};

const startFluoroRawDownload = (fileId, shareToken = null) => {
  if (fluoroRawCache.has(fileId)) {
    return Promise.resolve(fluoroRawCache.get(fileId));
  }
  if (fluoroRawLoading.has(fileId)) {
    return fluoroRawLoading.get(fileId);
  }

  const url = shareToken
    ? shareFileServeUrl(shareToken, fileId, 0)
    : `${API}/dicom/series/${fileId}/slice/0/raw`;

  const promise = axios
    .get(url, { responseType: "blob" })
    .then((res) => {
      const blob = new Blob([res.data], { type: "application/dicom" });
      const blobUrl = URL.createObjectURL(blob);
      fluoroRawCache.set(fileId, blobUrl);
      fluoroRawLoading.delete(fileId);
      return blobUrl;
    })
    .catch((e) => {
      fluoroRawLoading.delete(fileId);
      throw e;
    });

  fluoroRawLoading.set(fileId, promise);
  return promise;
};

const pumpFluoroRawBgQueue = () => {
  while (fluoroRawBgActive < FLUORO_RAW_BG_MAX && fluoroRawBgQueue.length > 0) {
    const nextId = fluoroRawBgQueue.shift();
    if (!nextId || fluoroRawCache.has(nextId) || fluoroRawLoading.has(nextId)) continue;
    fluoroRawBgActive += 1;
    startFluoroRawDownload(nextId)
      .catch(() => {})
      .finally(() => {
        fluoroRawBgActive = Math.max(0, fluoroRawBgActive - 1);
        pumpFluoroRawBgQueue();
      });
  }
};

/**
 * Get slice-0 raw DICOM.
 * urgent=true → start immediately (viewer playback).
 * urgent=false → background queue so previews don't block play.
 */
const prefetchFluoroRawSlice0 = (fileId, { urgent = false, shareToken = null } = {}) => {
  if (!fileId) return Promise.resolve(null);
  if (fluoroRawCache.has(fileId)) {
    return Promise.resolve(fluoroRawCache.get(fileId));
  }
  if (fluoroRawLoading.has(fileId)) {
    return fluoroRawLoading.get(fileId);
  }
  if (urgent) {
    const idx = fluoroRawBgQueue.indexOf(fileId);
    if (idx >= 0) fluoroRawBgQueue.splice(idx, 1);
    return startFluoroRawDownload(fileId, shareToken);
  }
  if (!fluoroRawBgQueue.includes(fileId)) {
    fluoroRawBgQueue.push(fileId);
  }
  pumpFluoroRawBgQueue();
  return Promise.resolve(null);
};

export default function ImageViewer({
  shareToken = null,
  shareStudy = null,
  shareStudies = null,
  shareFiles = null,
  sharePatient = null,
  onBack = null,
} = {}) {
  const { studyId: routeStudyId } = useParams();
  const isShare = Boolean(shareToken);
  const studyId = isShare ? null : routeStudyId;
  const navigate = useNavigate();
  const { getModality } = useModalities();
  const { canExportCsv } = useRbac();
  const canDownloadFiles = isShare || canExportCsv;
  const [study, setStudy] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [studyFilter, setStudyFilter] = useState(studyId || "all");
  const [studies, setStudies] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dicomUrl, setDicomUrl] = useState(null);
  const [serverMetadata, setServerMetadata] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [fetchingDicom, setFetchingDicom] = useState(false);
  const [viewerDecoding, setViewerDecoding] = useState(false);
  const dicomBlobUrlRef = useRef(null);
  const [seriesSlices, setSeriesSlices] = useState([]);
  const [sliceIdx, setSliceIdx] = useState(0);
  const [sliceImgUrl, setSliceImgUrl] = useState(null);
  const [slicePlaying, setSlicePlaying] = useState(false);
  const [sliceFps, setSliceFps] = useState(10);
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState(() => new Set());
  const [exportingSelectedFiles, setExportingSelectedFiles] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [officeDocModalOpen, setOfficeDocModalOpen] = useState(false);
  const [volumeScrollTotal, setVolumeScrollTotal] = useState(null);
  const [pendingVolumeSeek, setPendingVolumeSeek] = useState(null);
  const [mprOrientation, setMprOrientation] = useState("axial");
  // After first Coronal/Sagittal load, keep the volume in memory for all planes.
  const [volumeCached, setVolumeCached] = useState(false);
  const [patientRecordName, setPatientRecordName] = useState(null);
  const debounceRef = useRef(null);
  const metaDebounceRef = useRef(null);
  const dicomFullscreenRef = useRef(null);
  const isMultiFile = selectedFile?.series_type === "multi_file";

  const modalityConfig = useMemo(
    () => getModality(study?.modality ?? selectedFile?.modality),
    [getModality, study?.modality, selectedFile?.modality],
  );
  const viewMode = modalityConfig?.view_mode ?? "scroll";
  const modalityKey = modalityConfig?.key ?? study?.modality ?? selectedFile?.modality ?? "";
  const modalityLabel = modalityConfig?.label ?? "";
  const isFluoroStudy = modalityKey === "fluoro"
    || /fluoro|fluro/i.test(String(modalityLabel))
    || /fluoro|fluro/i.test(String(study?.modality ?? ""))
    || viewMode === "running";
  const hideDrawTools = modalityKey === "fluoro"
    || modalityKey === "pre_echo"
    || modalityKey === "post_echo"
    || viewMode === "running";
  const isScrollSeriesStudy = viewMode === "scroll"
    && (modalityKey === "ct_images" || modalityKey === "3mensio_ct" || modalityKey === "mri");
  // Raw CT / 3Mensio: per-file download in sidebar instead of export-video selection.
  const isPerFileDownloadStudy = modalityKey === "ct_images" || modalityKey === "3mensio_ct";
  const showExportVideosUi = Boolean(modalityConfig?.export_video) && !isPerFileDownloadStudy && !isShare && canDownloadFiles;
  // Fluoro downloads the blob before DWV parses — show a loader instead of "No DICOM loaded".
  const fluoroAwaitingSource = Boolean(isFluoroStudy && fetchingDicom);

  useEffect(() => {
    setDicomPreviewPaused(fetchingDicom);
  }, [fetchingDicom]);
  // Only pass volume URLs when user picks Coronal/Sagittal — keeps Axial fast.
  const seriesVolumeUrls = useMemo(() => {
    if (!isScrollSeriesStudy || !isMultiFile || !selectedFile?.id) return null;
    if (!seriesSlices?.length || seriesSlices.length < 2) return null;
    return seriesSlices.map(
      (_, i) => (isShare
        ? shareFileServeUrl(shareToken, selectedFile.id, i)
        : `${API}/dicom/series/${selectedFile.id}/slice/${i}/raw`),
    );
  }, [isScrollSeriesStudy, isMultiFile, selectedFile?.id, seriesSlices, isShare, shareToken]);
  // Keep volume mounted after first MPR open so Axial↔Coronal↔Sagittal is instant.
  const useVolumeMpr = Boolean(
    seriesVolumeUrls?.length && (volumeCached || mprOrientation !== "axial"),
  );
  const volumeUrls = useVolumeMpr ? seriesVolumeUrls : null;
  const seriesScrollCount = useVolumeMpr && volumeScrollTotal > 1
    ? volumeScrollTotal
    : (seriesSlices?.length || 0);
  const seriesScrollMax = Math.max(0, seriesScrollCount - 1);
  const mprPlanes = [
    { id: "axial", label: "Axial" },
    { id: "coronal", label: "Coronal" },
    { id: "sagittal", label: "Sagittal" },
  ];

  useEffect(() => {
    if (sliceIdx <= seriesScrollMax) return;
    setSliceIdx(seriesScrollMax);
    sliceIdxRef.current = seriesScrollMax;
  }, [seriesScrollMax, sliceIdx]);

  useEffect(() => {
    return () => {
      if (dicomBlobUrlRef.current) {
        URL.revokeObjectURL(dicomBlobUrlRef.current);
        dicomBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isShare) return;
    if (studyId) {
      axios.get(`${API}/studies/${studyId}`)
        .then(res => {
          const s = res.data.study;
          setStudy(s);
          if (s) {
            setStudies(prev => {
              const exists = prev.find(x => x.id === studyId);
              const entry = { id: studyId, label: s.description || studyId, patient_uhid: s.patient_uhid };
              if (exists) return prev.map(x => x.id === studyId ? entry : x);
              return [entry, ...prev];
            });
          }
        })
        .catch(() => {});
    }
  }, [isShare, studyId]);

  useEffect(() => {
    if (isShare) {
      if (sharePatient?.name) setPatientRecordName(sharePatient.name);
      else setPatientRecordName(null);
      return undefined;
    }
    const uhid = study?.patient_uhid || selectedFile?.patient_uhid;
    if (!uhid) {
      setPatientRecordName(null);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${API}/patients/${encodeURIComponent(uhid)}`)
      .then((res) => {
        if (!cancelled) setPatientRecordName(res.data?.patient?.name || null);
      })
      .catch(() => {
        if (!cancelled) setPatientRecordName(null);
      });
    return () => { cancelled = true; };
  }, [isShare, sharePatient?.name, study?.patient_uhid, selectedFile?.patient_uhid]);

  const patientDisplayName = useMemo(() => {
    if (isMissingPatientValue(patientRecordName)) return null;
    return String(patientRecordName).trim();
  }, [patientRecordName]);

  const patientUhid = study?.patient_uhid || selectedFile?.patient_uhid || "";

  const handleBackToPatient = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (isShare) return;
    if (patientUhid) {
      navigate(`/patients/${encodeURIComponent(patientUhid)}`);
      return;
    }
    navigate("/patients");
  };

  useEffect(() => {
    if (!isShare) return;
    const studiesList = (shareStudies?.length
      ? shareStudies
      : (shareStudy ? [shareStudy] : [])
    ).map((s) => ({
      id: s.id,
      label: s.description || s.modality || s.id,
      patient_uhid: s.patient_uhid,
    }));
    setStudies(studiesList);
    if (shareStudy) setStudy(shareStudy);
    else if (shareStudies?.length === 1) setStudy(shareStudies[0]);

    const filesFromStudies = (shareStudies || []).flatMap((s) => (
      (s.files || []).map((f) => ({
        ...f,
        study_id: f.study_id || s.id,
        modality: f.modality || s.modality,
      }))
    ));
    const files = (shareFiles?.length ? shareFiles : filesFromStudies).map((f) => ({
      ...f,
      original_filename: f.original_filename || f.filename,
    }));
    setAllFiles(files);
    if (studiesList.length === 1) setStudyFilter(studiesList[0].id);
    else setStudyFilter("all");
  }, [isShare, shareStudies, shareFiles, shareStudy]);

  useEffect(() => {
    if (isShare) return;
    const fetchAll = async () => {
      const params = { limit: 200 };
      if (studyId) params.study_id = studyId;

      const [filesRes, dicomRes, studiesRes] = await Promise.allSettled([
        axios.get(`${API}/files`, { params }),
        axios.get(`${API}/dicom/files`),
        axios.get(`${API}/studies`, { params: { limit: 200 } }),
      ]);

      const studyMap = {};
      if (studiesRes.status === "fulfilled") {
        for (const s of studiesRes.value.data.studies || []) {
          studyMap[s.id] = { description: s.description, patient_uhid: s.patient_uhid, modality: s.modality };
        }
      }

      const dicomById = {};
      if (dicomRes.status === "fulfilled") {
        for (const f of dicomRes.value.data.files || []) {
          dicomById[f.id] = f;
        }
      }

      if (filesRes.status === "fulfilled") {
        const rawFiles = filesRes.value.data.files || [];
        const merged = rawFiles.map((f) => ({
          ...f,
          ...(dicomById[f.id] || {}),
          id: f.id,
          original_filename: f.original_filename || f.filename,
        }));

        const sampleDicom = (dicomRes.status === "fulfilled" ? dicomRes.value.data.files : [])
          ?.filter((f) => f.is_sample && !merged.find((m) => m.id === f.id)) ?? [];
        const files = [...sampleDicom, ...merged];

        setAllFiles(files);

        const seen = new Set();
        const studyList = [];
        files.forEach(f => {
          if (f.study_id && !seen.has(f.study_id)) {
            seen.add(f.study_id);
            const meta = studyMap[f.study_id];
            studyList.push({
              id: f.study_id,
              label: meta?.description || f.study_id,
              patient_uhid: meta?.patient_uhid || f.patient_uhid || "",
            });
          }
        });
        if (studyList.length > 0) setStudies(studyList);
      }
    };
    fetchAll();
  }, [isShare, studyId]);

  useEffect(() => {
    if (studyId) setStudyFilter(studyId);
  }, [studyId]);

  useEffect(() => {
    setSelectedFileIds(new Set());
  }, [studyFilter]);

  const studyFiles = studyFilter === "all"
    ? allFiles
    : allFiles.filter(f => f.study_id === studyFilter);

  const sliceBlobRef = useRef(null);
  const sliceAbortRef = useRef(null);
  const dicomFetchAbortRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const sliceCacheRef = useRef(new Map()); // idx -> blobUrl
  const sliceLoadingRef = useRef(new Map()); // idx -> Promise<string>
  const sliceIdxRef = useRef(0);
  const slicePlayingRef = useRef(false);

  const clearSliceCache = useCallback(() => {
    for (const url of sliceCacheRef.current.values()) {
      // Sidebar-prefetched fluoro blobs are shared — do not revoke them.
      if (isFluoroRawUrl(url)) continue;
      try { URL.revokeObjectURL(url); } catch (_) {}
    }
    sliceCacheRef.current.clear();
    sliceLoadingRef.current.clear();
    sliceBlobRef.current = null;
  }, []);

  const pruneSliceCache = useCallback((centerIdx) => {
    const maxKeep = 48;
    if (sliceCacheRef.current.size <= maxKeep) return;
    const ranked = [...sliceCacheRef.current.keys()].sort(
      (a, b) => Math.abs(a - centerIdx) - Math.abs(b - centerIdx),
    );
    for (const idx of ranked.slice(maxKeep)) {
      const url = sliceCacheRef.current.get(idx);
      if (url && url !== sliceBlobRef.current && !isFluoroRawUrl(url)) {
        try { URL.revokeObjectURL(url); } catch (_) {}
      }
      sliceCacheRef.current.delete(idx);
    }
  }, []);

  const ensureSeriesSlice = useCallback(async (fileInfo, idx) => {
    if (!fileInfo?.id) return null;
    if (sliceCacheRef.current.has(idx)) {
      return sliceCacheRef.current.get(idx);
    }
    if (sliceLoadingRef.current.has(idx)) {
      return sliceLoadingRef.current.get(idx);
    }

    // Reuse sidebar-prefetched slice 0; urgent so viewer isn't stuck behind bg prefetches.
    if (!isShare && idx === 0) {
      const promise = prefetchFluoroRawSlice0(fileInfo.id, { urgent: true }).then((blobUrl) => {
        if (blobUrl) sliceCacheRef.current.set(0, blobUrl);
        sliceLoadingRef.current.delete(0);
        return blobUrl;
      }).catch((e) => {
        sliceLoadingRef.current.delete(0);
        throw e;
      });
      sliceLoadingRef.current.set(0, promise);
      return promise;
    }

    const url = isShare
      ? shareFileServeUrl(shareToken, fileInfo.id, idx)
      : `${API}/dicom/series/${fileInfo.id}/slice/${idx}/raw`;

    const promise = axios
      .get(url, {
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/dicom" });
        const blobUrl = URL.createObjectURL(blob);
        sliceCacheRef.current.set(idx, blobUrl);
        sliceLoadingRef.current.delete(idx);
        pruneSliceCache(idx);
        return blobUrl;
      })
      .catch((e) => {
        sliceLoadingRef.current.delete(idx);
        throw e;
      });

    sliceLoadingRef.current.set(idx, promise);
    return promise;
  }, [pruneSliceCache, isShare, shareToken]);

  // Prefetch both ways so scrolling up stays as smooth as scrolling down.
  const prefetchSeriesSlices = useCallback((fileInfo, fromIdx, ahead = 8, behind = 4) => {
    if (!fileInfo?.id || !seriesSlices.length) return;
    const count = seriesSlices.length;
    for (let i = 1; i <= ahead; i += 1) {
      ensureSeriesSlice(fileInfo, (fromIdx + i) % count).catch(() => {});
    }
    for (let i = 1; i <= behind; i += 1) {
      ensureSeriesSlice(fileInfo, (fromIdx - i + count) % count).catch(() => {});
    }
  }, [ensureSeriesSlice, seriesSlices.length]);

  const loadSeriesSlice = useCallback(async (fileInfo, idx) => {
    if (!fileInfo?.id) return;
    sliceIdxRef.current = idx;
    try {
      const blobUrl = await ensureSeriesSlice(fileInfo, idx);
      // Ignore stale navigation if user moved on while fetching.
      if (sliceIdxRef.current !== idx) return;
      sliceBlobRef.current = blobUrl;
      setSliceImgUrl(blobUrl);
      if (!isShare) {
        axios.get(`${API}/dicom/series/${fileInfo.id}/slice/${idx}/metadata`)
          .then((resMeta) => setServerMetadata(resMeta.data.metadata))
          .catch(() => {});
      }
      prefetchSeriesSlices(fileInfo, idx);
    } catch (e) {
      if (axios.isCancel(e) || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      console.error("Slice load failed:", e?.response?.status, e?.message);
    }
  }, [ensureSeriesSlice, prefetchSeriesSlices, isShare]);

  /**
   * Paint an already-cached slice right away. Scrolling debounces the network
   * load, so without this every intermediate slice is skipped and only the one
   * the user stopped on ever renders.
   */
  const showCachedSeriesSlice = useCallback((fileInfo, idx) => {
    if (!fileInfo?.id) return false;
    const blobUrl = sliceCacheRef.current.get(idx);
    if (!blobUrl) return false;

    sliceIdxRef.current = idx;
    sliceBlobRef.current = blobUrl;
    setSliceImgUrl(blobUrl);
    prefetchSeriesSlices(fileInfo, idx);

    // Tags trail the image: one metadata request per slice the user settles on.
    if (!isShare) {
      clearTimeout(metaDebounceRef.current);
      metaDebounceRef.current = setTimeout(() => {
        axios.get(`${API}/dicom/series/${fileInfo.id}/slice/${idx}/metadata`)
          .then((resMeta) => setServerMetadata(resMeta.data.metadata))
          .catch(() => {});
      }, 200);
    }

    return true;
  }, [prefetchSeriesSlices, isShare]);

  const loadDicomFile = useCallback(async (fileInfo) => {
    const requestId = ++loadRequestIdRef.current;
    setSelectedFile(fileInfo);
    setServerMetadata(null);
    setSeriesSlices([]);
    setSliceIdx(0);
    sliceIdxRef.current = 0;
    setSlicePlaying(false);
    slicePlayingRef.current = false;
    setVolumeScrollTotal(null);
    setPendingVolumeSeek(null);
    setMprOrientation("axial");
    setVolumeCached(false);
    clearSliceCache();
    setSliceImgUrl(null);
    clearTimeout(debounceRef.current);
    clearTimeout(metaDebounceRef.current);

    if (dicomFetchAbortRef.current) dicomFetchAbortRef.current.abort();
    if (sliceAbortRef.current) sliceAbortRef.current.abort();

    if (fileInfo.file_type === "video") {
      setFetchingDicom(false);
      setDicomUrl(null);
      return;
    }
    if (viewMode === "pdf" || viewMode === "document") {
      setFetchingDicom(false);
      setDicomUrl(null);
      return;
    }
    if (fileInfo.file_type === "pdf" && viewMode === "pdf") {
      setFetchingDicom(false);
      setDicomUrl(null);
      return;
    }

    if (fileInfo.series_type === "multi_file") {
      setDicomUrl(null);
      setFetchingDicom(true);
      try {
        let slices = [];
        if (isShare) {
          slices = (fileInfo.slice_files || []).map((s, i) => ({
            index: s.index ?? i,
            num_frames: s.num_frames || 1,
          }));
          if (!slices.length && fileInfo.slice_count > 0) {
            slices = Array.from({ length: fileInfo.slice_count }, (_, i) => ({
              index: i,
              num_frames: 1,
            }));
          }
        } else {
          const res = await axios.get(`${API}/dicom/series/${fileInfo.id}/slices`);
          slices = res.data.slices || [];
        }
        if (requestId !== loadRequestIdRef.current) return;
        setSeriesSlices(slices);
        // Always open first axial slice immediately. Volume builds only when
        // user picks Coronal/Sagittal in the left panel.
        await loadSeriesSlice(fileInfo, 0);
      } catch {
        if (requestId !== loadRequestIdRef.current) return;
        toast.error("Failed to load series slices");
      } finally {
        if (requestId === loadRequestIdRef.current) setFetchingDicom(false);
      }
      return;
    }

    if (fileInfo.file_type !== "dicom" && !fileInfo.is_sample) {
      setFetchingDicom(false);
      setDicomUrl(null);
      setSliceImgUrl(null);
      return;
    }

    setSliceImgUrl(null);
    setDicomUrl(null); // abort previous DWV load; revoke old blob after delay below
    setLoadingMeta(true);
    setFetchingDicom(true);
    const controller = new AbortController();
    dicomFetchAbortRef.current = controller;
    const url = fileInfo.id === "sample"
      ? `${API}/dicom/sample`
      : (isShare ? shareFileServeUrl(shareToken, fileInfo.id) : `${API}/files/${fileInfo.id}/serve`);
    try {
      const fileRes = await axios.get(url, {
        responseType: "blob",
        signal: controller.signal,
      });
      if (requestId !== loadRequestIdRef.current) return;
      const blob = new Blob([fileRes.data], { type: "application/dicom" });
      const blobUrl = URL.createObjectURL(blob);
      const prevUrl = dicomBlobUrlRef.current;
      dicomBlobUrlRef.current = blobUrl;
      setDicomUrl(blobUrl);
      // Don't revoke while DWV may still be decoding the previous blob
      if (prevUrl) {
        window.setTimeout(() => URL.revokeObjectURL(prevUrl), 1500);
      }
    } catch (e) {
      if (axios.isCancel(e) || e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (requestId !== loadRequestIdRef.current) return;
      toast.error("Failed to load DICOM file");
    } finally {
      if (requestId === loadRequestIdRef.current) setFetchingDicom(false);
    }
    if (!isShare) {
      try {
        const res = await axios.get(`${API}/dicom/${fileInfo.id}/metadata`, {
          signal: controller.signal,
        });
        if (requestId !== loadRequestIdRef.current) return;
        setServerMetadata(res.data.metadata);
      } catch (_) {}
    }
    if (requestId === loadRequestIdRef.current) setLoadingMeta(false);
  }, [loadSeriesSlice, viewMode, clearSliceCache, isShare, shareToken]);

  const selectFile = useCallback((fileInfo) => {
    setSelectedFile(fileInfo);
    if (fileInfo.file_type === "video") return;
    if (viewMode === "pdf" || viewMode === "document") return;
    if (fileInfo.file_type === "dicom" || fileInfo.series_type === "multi_file" || fileInfo.is_sample) {
      if (fileInfo.series_type !== "multi_file") {
        setViewerDecoding(true);
        setDicomPreviewPaused(true);
      }
      loadDicomFile(fileInfo);
    }
  }, [loadDicomFile, viewMode]);

  useEffect(() => {
    if (studyFiles.length === 0) return;
    if (selectedFile && studyFiles.find(f => f.id === selectedFile.id)) return;
    const studyFile = studyId && studyFiles.find(f => f.study_id === studyId);
    const sample = studyFiles.find(f => f.is_sample);
    selectFile(studyFile || sample || studyFiles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyFiles]);

  useEffect(() => {
    sliceIdxRef.current = sliceIdx;
  }, [sliceIdx]);

  useEffect(() => {
    slicePlayingRef.current = slicePlaying;
  }, [slicePlaying]);

  useEffect(() => {
    if (!isMultiFile || !seriesSlices.length || useVolumeMpr) return undefined;
    const showOrLoad = (next) => {
      clearTimeout(debounceRef.current);
      if (showCachedSeriesSlice(selectedFile, next)) return;
      debounceRef.current = setTimeout(() => loadSeriesSlice(selectedFile, next), 80);
    };
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        setSlicePlaying(false);
        setSliceIdx(i => {
          const next = Math.min(i + 1, seriesSlices.length - 1);
          showOrLoad(next);
          return next;
        });
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        setSlicePlaying(false);
        setSliceIdx(i => {
          const next = Math.max(i - 1, 0);
          showOrLoad(next);
          return next;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isMultiFile, seriesSlices, selectedFile, loadSeriesSlice, showCachedSeriesSlice, useVolumeMpr]);

  const handleVolumeFrameChange = useCallback((f) => {
    if (typeof f !== "number" || !Number.isFinite(f)) return;
    // Display-only sync from viewer; do not feed back as a seek.
    setSliceIdx((prev) => (prev === f ? prev : f));
    sliceIdxRef.current = f;
  }, []);

  const handleVolumeScrollMeta = useCallback(({ total }) => {
    if (typeof total !== "number" || total <= 1) return;
    setVolumeScrollTotal((prev) => (prev === total ? prev : total));
  }, []);

  // One-shot seek for volume MPR — set only on user slider/buttons/play, then cleared.
  const requestVolumeSeek = useCallback((frame) => {
    const next = Math.max(0, Math.min(seriesScrollMax, frame));
    setSliceIdx(next);
    sliceIdxRef.current = next;
    setPendingVolumeSeek(next);
  }, [seriesScrollMax]);
  const handleVolumeSeekApplied = useCallback(() => {
    setPendingVolumeSeek(null);
  }, []);

  const handleSelectMprPlane = useCallback((ori) => {
    setSlicePlaying(false);
    setMprOrientation(ori);
    if (ori !== "axial") {
      // First Coronal/Sagittal builds the volume; later plane switches reuse it.
      setVolumeCached(true);
      return;
    }
    // Axial before volume exists → keep fast single-slice mode.
    if (!volumeCached && selectedFile?.series_type === "multi_file") {
      setVolumeScrollTotal(null);
      setPendingVolumeSeek(null);
      loadSeriesSlice(selectedFile, sliceIdxRef.current).catch(() => {});
    }
  }, [selectedFile, loadSeriesSlice, volumeCached]);

  // Wait for each slice to load before advancing — prevents abort/race during play.
  // Volume MPR mode: DWV holds the full stack; play only advances frameIndex.
  useEffect(() => {
    if (!slicePlaying || !isScrollSeriesStudy || !isMultiFile || seriesSlices.length <= 1 || !selectedFile) {
      return undefined;
    }
    if (useVolumeMpr) {
      const intervalMs = Math.max(40, Math.round(1000 / sliceFps));
      const id = window.setInterval(() => {
        const cur = sliceIdxRef.current;
        const next = seriesScrollCount ? (cur + 1) % seriesScrollCount : 0;
        requestVolumeSeek(next);
      }, intervalMs);
      return () => window.clearInterval(id);
    }
    let cancelled = false;
    const playNext = async () => {
      while (!cancelled && slicePlayingRef.current) {
        const next = (sliceIdxRef.current + 1) % seriesSlices.length;
        setSliceIdx(next);
        try {
          await loadSeriesSlice(selectedFile, next);
        } catch (_) {}
        if (cancelled || !slicePlayingRef.current) break;
        await new Promise((r) => setTimeout(r, Math.max(40, Math.round(1000 / sliceFps))));
      }
    };
    playNext();
    return () => { cancelled = true; };
  }, [
    slicePlaying,
    isScrollSeriesStudy,
    isMultiFile,
    seriesSlices.length,
    selectedFile,
    loadSeriesSlice,
    sliceFps,
    useVolumeMpr,
    seriesScrollCount,
    requestVolumeSeek,
  ]);

  const goToSeriesSlice = useCallback((idx) => {
    if (!selectedFile || !seriesScrollCount) return;
    const next = Math.max(0, Math.min(seriesScrollMax, idx));
    if (useVolumeMpr) {
      requestVolumeSeek(next);
      return;
    }
    setSliceIdx(next);
    sliceIdxRef.current = next;
    if (showCachedSeriesSlice(selectedFile, next)) return;
    loadSeriesSlice(selectedFile, next);
  }, [
    selectedFile,
    seriesScrollCount,
    seriesScrollMax,
    loadSeriesSlice,
    showCachedSeriesSlice,
    useVolumeMpr,
    requestVolumeSeek,
  ]);

  const stepSeriesSlice = useCallback((forward) => {
    setSlicePlaying(false);
    const cur = sliceIdxRef.current;
    if (!seriesScrollCount) return;
    const next = forward
      ? Math.min(cur + 1, seriesScrollMax)
      : Math.max(cur - 1, 0);
    if (useVolumeMpr) {
      requestVolumeSeek(next);
      return;
    }
    setSliceIdx(next);
    sliceIdxRef.current = next;
    if (selectedFile) {
      clearTimeout(debounceRef.current);
      // Cached slices show at once so a continuous scroll animates; only a
      // slice that still needs fetching waits for the scroll to settle.
      if (showCachedSeriesSlice(selectedFile, next)) return;
      debounceRef.current = setTimeout(() => loadSeriesSlice(selectedFile, next), 80);
    }
  }, [
    seriesScrollCount,
    seriesScrollMax,
    selectedFile,
    loadSeriesSlice,
    showCachedSeriesSlice,
    useVolumeMpr,
    requestVolumeSeek,
  ]);

  const discussionStudyId = studyId || study?.id || null;
  const selectedStudyFiles = useMemo(
    () => studyFiles.filter((f) => selectedFileIds.has(f.id)),
    [studyFiles, selectedFileIds],
  );
  const hasSelectedStudyFiles = selectedStudyFiles.length > 0;

  const filterLabel = studyFilter === "all"
    ? "All Studies"
    : (studies.find(s => s.id === studyFilter)?.label || study?.description || studyFilter);

  const dicomFullscreenClass =
    "h-full flex-1 flex flex-col min-w-0 min-h-0 [&:fullscreen]:h-screen [&:fullscreen]:w-full [&:fullscreen]:bg-background [&:fullscreen]:p-3 [&:fullscreen]:gap-2 [&:fullscreen]:overflow-hidden";

  const handleToggleFileSelection = useCallback((fileId) => {
    if (!fileId) return;
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const handleSelectAllStudyFiles = useCallback(() => {
    setSelectedFileIds(new Set(studyFiles.map((f) => f.id)));
  }, [studyFiles]);

  const handleClearSelectedStudyFiles = useCallback(() => {
    setSelectedFileIds(new Set());
  }, []);

  const handleExportSelectedFiles = useCallback(async () => {
    if (!hasSelectedStudyFiles) {
      toast.error("Select at least one file to export");
      return;
    }

    const byStudy = new Map();
    for (const file of selectedStudyFiles) {
      const sid = file?.study_id || studyId || study?.id;
      if (!sid || !file?.id) continue;
      if (!byStudy.has(sid)) byStudy.set(sid, []);
      byStudy.get(sid).push(file.id);
    }

    if (byStudy.size === 0) {
      toast.error("Select at least one file to export");
      return;
    }

    setExportingSelectedFiles(true);
    try {
      for (const [sid, fileIds] of byStudy) {
        const response = await axios.post(
          `${API}/dicom/study/${sid}/export-all-videos`,
          { format: "mp4", fps: 15, file_ids: fileIds },
          { responseType: "blob" },
        );

        const blob = new Blob([response.data], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "selected_files.zip";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }

      toast.success(
        `Exported ${selectedStudyFiles.length} file${selectedStudyFiles.length !== 1 ? "s" : ""}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to export videos");
    } finally {
      setExportingSelectedFiles(false);
    }
  }, [hasSelectedStudyFiles, selectedStudyFiles, studyId, study?.id]);

  const handleDownloadStudyFile = useCallback(async (file) => {
    if (!file?.id) return;
    setDownloadingFileId(file.id);
    try {
      const res = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: "blob",
      });

      const disposition = res.headers?.["content-disposition"];
      let filename = file.original_filename || file.filename || `${file.id}.dcm`;
      if (file.series_type === "multi_file" && !/\.zip$/i.test(filename)) {
        filename = `${filename}.zip`;
      }
      const match = disposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      if (match?.[1]) filename = decodeURIComponent(match[1]);

      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloadingFileId(null);
    }
  }, []);

  useEffect(() => {
    if (!selectedFile || viewMode !== "document") {
      setOfficeDocModalOpen(false);
      return;
    }
    const name = selectedFile.original_filename || selectedFile.filename || "";
    if (isOfficeDocumentFile(name)) {
      setOfficeDocModalOpen(true);
    } else {
      setOfficeDocModalOpen(false);
    }
  }, [selectedFile?.id, viewMode, selectedFile?.original_filename, selectedFile?.filename]);

  const renderMainViewer = () => {
    if (!selectedFile) {
      return (
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground min-h-[40vh]">
          Select a file to view
        </div>
      );
    }

    if (selectedFile.file_type === "video") {
      return (
        <VideoPlayer
          fileId={selectedFile.id}
          fileName={selectedFile.original_filename}
          aviMeta={selectedFile.avi_meta}
          serveUrl={isShare ? shareFileServeUrl(shareToken, selectedFile.id) : undefined}
        />
      );
    }

    if (viewMode === "document") {
      const name = selectedFile.original_filename || selectedFile.filename || "";
      const serveUrl = isShare ? shareFileServeUrl(shareToken, selectedFile.id) : undefined;
      if (isPdfFile(name) || selectedFile.file_type === "pdf") {
        return (
          <PdfViewer
            fileId={selectedFile.id}
            fileName={selectedFile.original_filename}
            serveUrl={serveUrl}
          />
        );
      }
      if (isOfficeDocumentFile(name)) {
        return (
          <>
            <div className="flex flex-col flex-1 items-center justify-center gap-4 p-6 min-h-[40vh] text-center">
              <p className="text-sm font-medium text-[var(--xl-text)] break-all max-w-md">{name}</p>
              <Button type="button" onClick={() => setOfficeDocModalOpen(true)} data-testid="open-office-document-preview">
                View document
              </Button>
            </div>
            <OfficeDocumentViewerModal
              open={officeDocModalOpen}
              onOpenChange={setOfficeDocModalOpen}
              file={selectedFile}
              previewUrl={serveUrl}
              serveUrl={serveUrl}
            />
          </>
        );
      }
      return (
        <DocumentDownload
          fileId={selectedFile.id}
          fileName={selectedFile.original_filename}
          fileSize={selectedFile.file_size}
          mimeType={selectedFile.mime_type}
          serveUrl={serveUrl}
        />
      );
    }

    if (viewMode === "pdf" || selectedFile.file_type === "pdf") {
      return (
        <PdfViewer
          fileId={selectedFile.id}
          fileName={selectedFile.original_filename}
          serveUrl={isShare ? shareFileServeUrl(shareToken, selectedFile.id) : undefined}
        />
      );
    }

    if (isMultiFile) {
      const seriesBottomBar = !isFluoroStudy && seriesSlices.length > 1 ? (
        <div
          className="bg-zinc-950/95 border-t border-zinc-800 px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3"
          data-testid="series-slice-controls"
        >
          {isScrollSeriesStudy ? (
            <>
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
                  onClick={() => {
                    setSlicePlaying(false);
                    goToSeriesSlice(0);
                  }}
                  aria-label="First slice"
                  data-testid="slice-first"
                >
                  <SkipBack className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
                  onClick={() => stepSeriesSlice(false)}
                  aria-label="Previous slice"
                  data-testid="slice-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:bg-primary/15"
                  onClick={() => setSlicePlaying((p) => !p)}
                  aria-label={slicePlaying ? "Pause scroll" : "Play scroll"}
                  data-testid="slice-play"
                >
                  {slicePlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-300 hover:text-white hover:bg-zinc-800"
                  onClick={() => stepSeriesSlice(true)}
                  aria-label="Next slice"
                  data-testid="slice-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                <span className="text-[11px] text-zinc-300 w-10 text-right tabular-nums font-medium">
                  {Math.min(sliceIdx, seriesScrollMax) + 1}
                </span>
                <Slider
                  min={0}
                  max={seriesScrollMax}
                  step={1}
                  value={[Math.min(sliceIdx, seriesScrollMax)]}
                  onValueChange={([v]) => {
                    if (typeof v !== "number" || v === sliceIdxRef.current) return;
                    setSlicePlaying(false);
                    goToSeriesSlice(v);
                  }}
                  className="flex-1"
                  data-testid="slice-slider"
                />
                <span className="text-[11px] text-zinc-400 w-10 tabular-nums">{seriesScrollCount}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-zinc-400 hidden sm:inline">FPS</span>
                <select
                  value={sliceFps}
                  onChange={(e) => setSliceFps(Number(e.target.value))}
                  className="h-8 text-[11px] bg-zinc-800 border border-zinc-600 rounded-md px-2 text-zinc-100 focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="slice-fps"
                >
                  {[5, 8, 10, 15, 24].map((v) => (
                    <option key={v} value={v} className="bg-zinc-900">{v}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <span className="text-[10px] text-muted-foreground w-8 text-right">{sliceIdx + 1}</span>
              <Slider
                min={0}
                max={seriesSlices.length - 1}
                step={1}
                value={[sliceIdx]}
                onValueChange={([v]) => {
                  setSliceIdx(v);
                  clearTimeout(debounceRef.current);
                  if (showCachedSeriesSlice(selectedFile, v)) return;
                  debounceRef.current = setTimeout(() => loadSeriesSlice(selectedFile, v), 300);
                }}
                className="flex-1"
              />
              <span className="text-[10px] text-muted-foreground w-8">{seriesSlices.length}</span>
              <span className="text-[10px] text-muted-foreground/50 ml-1">← → slices</span>
            </>
          )}
        </div>
      ) : null;

      return (
        <div ref={dicomFullscreenRef} className={dicomFullscreenClass}>
          <div className="flex-1 min-h-0 h-full">
            <DicomViewer
              key={`series-${selectedFile?.id}-${useVolumeMpr ? "vol" : "slice"}`}
              fileUrl={useVolumeMpr ? null : sliceImgUrl}
              volumeUrls={volumeUrls}
              knownNumFrames={useVolumeMpr ? seriesSlices.length : (seriesSlices[sliceIdx]?.num_frames || 1)}
              fileId={selectedFile?.id}
              fileName={selectedFile?.original_filename}
              patientName={patientDisplayName}
              seriesSliceIdx={useVolumeMpr ? null : sliceIdx}
              exportVideo={Boolean(modalityConfig?.export_video) && !isShare}
              fullscreenRef={dicomFullscreenRef}
              hideFilmstrip={isFluoroStudy}
              hideDrawTools={hideDrawTools}
              hidePlaybackBar={isScrollSeriesStudy}
              hideOrientationTool={isScrollSeriesStudy}
              autoPlayCine={isFluoroStudy}
              awaitingSource={fluoroAwaitingSource}
              hideLoadingOverlay={!useVolumeMpr && isScrollSeriesStudy && (slicePlaying || sliceIdx > 0)}
              seriesScrollEnabled={!useVolumeMpr && isScrollSeriesStudy && seriesSlices.length > 1}
              onSeriesScroll={useVolumeMpr ? undefined : stepSeriesSlice}
              frameIndex={useVolumeMpr ? pendingVolumeSeek : undefined}
              onFrameChange={useVolumeMpr ? handleVolumeFrameChange : undefined}
              onScrollMetaChange={useVolumeMpr ? handleVolumeScrollMeta : undefined}
              onSeekApplied={useVolumeMpr ? handleVolumeSeekApplied : undefined}
              externalOrientation={useVolumeMpr ? mprOrientation : undefined}
              bottomBar={seriesBottomBar}
            />
          </div>
        </div>
      );
    }

    return (
      <div ref={dicomFullscreenRef} className={dicomFullscreenClass}>
        <DicomViewer
          key={`file-${selectedFile?.id}`}
          fileUrl={dicomUrl}
          knownNumFrames={selectedFile?.num_frames}
          fileId={selectedFile?.id}
          fileName={selectedFile?.original_filename}
          patientName={patientDisplayName}
          exportVideo={Boolean(modalityConfig?.export_video) && !isShare}
          fullscreenRef={dicomFullscreenRef}
          hideFilmstrip={isFluoroStudy}
          hideDrawTools={hideDrawTools}
          autoPlayCine={isFluoroStudy}
          awaitingSource={fluoroAwaitingSource}
          onDecodeStart={() => setViewerDecoding(true)}
          onDecodeReady={() => setViewerDecoding(false)}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 min-h-0 flex-1 h-full overflow-hidden" data-testid="image-viewer-page">

      <div className={`w-full flex-shrink-0 flex flex-col gap-1.5 h-52 sm:h-64 lg:h-auto lg:self-stretch lg:min-h-0 ${isFluoroStudy ? "lg:w-80" : "lg:w-60"}`} data-testid="viewer-file-list">
        <div className="min-h-10 flex items-center gap-1 px-1 flex-shrink-0">
          {(!isShare || onBack) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleBackToPatient}
            data-testid="viewer-back-btn"
            className="gap-1 h-9 px-2 -ml-2 flex-shrink-0"
            aria-label={isShare ? "Close viewer" : "Back to patient"}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{isShare ? "Close" : "Back"}</span>
          </Button>
          )}
          <h1
            className="text-xl sm:text-2xl font-bold tracking-tight text-[var(--xl-text)] truncate"
            data-testid="viewer-page-title"
          >
            Image Viewer
          </h1>
        </div>
        <Card className="flex-1 flex flex-col overflow-hidden min-h-0">
          <CardHeader className="py-3 px-3 space-y-2 flex-shrink-0">
            {modalityConfig && (
              <Badge variant="outline" className="text-[9px] w-fit">
                {modalityConfig.label} · {viewMode}
              </Badge>
            )}

            {showExportVideosUi ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAllStudyFiles}
                  className="h-6 px-2 text-[10px]"
                  disabled={studyFiles.length === 0}
                >
                  Select all
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClearSelectedStudyFiles}
                  className="h-6 px-2 text-[10px]"
                  disabled={!hasSelectedStudyFiles}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleExportSelectedFiles}
                  className="h-6 px-2 text-[10px]"
                  disabled={!hasSelectedStudyFiles || exportingSelectedFiles}
                >
                  {exportingSelectedFiles ? "Exporting..." : `Export videos${hasSelectedStudyFiles ? ` (${selectedStudyFiles.length})` : ""}`}
                </Button>
              </div>
            ) : null}

            {isScrollSeriesStudy && isMultiFile && seriesSlices.length > 1 && (
              <div className="space-y-1.5" data-testid="mpr-plane-controls">
                <p className="text-[10px] text-muted-foreground">View plane</p>
                <div className="grid grid-cols-3 gap-1">
                  {mprPlanes.map((plane) => (
                    <Button
                      key={plane.id}
                      type="button"
                      variant={mprOrientation === plane.id ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-1 text-[10px]"
                      onClick={() => handleSelectMprPlane(plane.id)}
                      aria-pressed={mprOrientation === plane.id}
                      data-testid={`mpr-${plane.id}`}
                    >
                      {plane.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardHeader>

          <div className="px-5 flex-shrink-0">
            <div className="h-px bg-[var(--xl-line)]" />
          </div>

          <ScrollArea className="flex-1 min-h-0 [&>[data-radix-scroll-area-viewport]>div]:!block">
            <div className="px-5 pb-2">
              {studyFiles.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">
                  {studyFilter === "all" ? "No files" : "No files for this study"}
                </p>
              ) : isFluoroStudy ? (
                <div className="grid grid-cols-2 gap-2 py-2">
                  {studyFiles.map((f, index) => (
                    <FluoroFilePreview
                      key={f.id}
                      file={f}
                      order={index}
                      active={selectedFile?.id === f.id}
                      viewerReady={Boolean(dicomUrl) && !viewerDecoding}
                      selected={selectedFileIds.has(f.id)}
                      onSelect={() => selectFile(f)}
                      onToggleSelect={() => handleToggleFileSelection(f.id)}
                      shareToken={shareToken}
                      hideSelect={isShare}
                    />
                  ))}
                </div>
              ) : (
                studyFiles.map((f, index) => (
                    <div key={f.id}>
                      {index > 0 ? (
                        <div className="h-px bg-[var(--xl-line)]" aria-hidden />
                      ) : null}
                      <button
                        type="button"
                        data-testid={`viewer-file-${f.id}`}
                        onClick={() => selectFile(f)}
                        className={`w-full text-left py-2 rounded-md transition-colors ${
                          selectedFile?.id === f.id
                            ? "bg-primary/10"
                            : "hover:bg-secondary/50"
                        }`}
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <p className="text-xs font-medium truncate min-w-0 flex-1">{f.original_filename}</p>
                          {(f.series_type === "multi_file" || f.num_frames > 1 || f.is_sample) && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {f.series_type === "multi_file" ? (
                                <Badge className="text-[9px] px-1 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                  <Layers className="w-2.5 h-2.5 mr-0.5" />{f.slice_count}s
                                </Badge>
                              ) : f.num_frames > 1 && (
                                <Badge className="text-[9px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <MonitorPlay className="w-2.5 h-2.5 mr-0.5" />{f.num_frames}f
                                </Badge>
                              )}
                              {f.is_sample && <Badge variant="secondary" className="text-[9px] px-1 py-0">Sample</Badge>}
                            </div>
                          )}
                        </div>
                      </button>
                      {isPerFileDownloadStudy && !isShare && canDownloadFiles ? (
                        <div className="pb-2">
                          <button
                            type="button"
                            className="inline-flex items-center text-muted-foreground hover:text-foreground disabled:opacity-50"
                            onClick={() => handleDownloadStudyFile(f)}
                            disabled={downloadingFileId === f.id}
                            aria-label={`Download ${f.original_filename}`}
                            data-testid={`download-file-${f.id}`}
                            title="Download"
                          >
                            {downloadingFileId === f.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      ) : showExportVideosUi ? (
                        <div className="pb-2">
                          <label className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedFileIds.has(f.id)}
                              onChange={() => handleToggleFileSelection(f.id)}
                              aria-label={`Select ${f.original_filename}`}
                            />
                            Select
                          </label>
                        </div>
                      ) : null}
                    </div>
                ))
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>

      <div className="flex-1 flex flex-col min-w-0 min-h-[40vh] sm:min-h-[50vh] lg:min-h-0 lg:self-stretch overflow-hidden">
        {renderMainViewer()}
      </div>

      {!isShare && discussionOpen ? (
        <div
          className="w-full lg:w-72 flex-shrink-0 h-64 sm:h-72 lg:h-auto lg:self-stretch lg:min-h-0"
          data-testid="viewer-right-panel"
        >
          <Card className="h-full overflow-hidden">
            <Tabs defaultValue="discussion" className="h-full flex flex-col">
              <div className="flex items-center gap-1 mx-2 mt-2 mb-0">
                <TabsList className="flex-1">
                  {/* <TabsTrigger value="info" data-testid="tab-info" className="text-xs gap-1">
                    <Info className="w-3 h-3" /> Info
                  </TabsTrigger> */}
                  <TabsTrigger value="discussion" data-testid="tab-discussion" className="text-xs gap-1">
                    <MessageSquare className="w-3 h-3" /> Discussion
                  </TabsTrigger>
                </TabsList>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => setDiscussionOpen(false)}
                  aria-label="Collapse discussion"
                  data-testid="collapse-discussion"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

            {/* <TabsContent value="info" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-3 space-y-3">
                  {study && (
                    <>
                      <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Study</h4>
                      <MetaRow label="Description" value={study.description} />
                      <MetaRow label="Patient" value={study.patient_uhid} mono />
                      <MetaRow label="Modality" value={getModality(study.modality)?.label ?? study.modality} />
                      <MetaRow label="View Mode" value={viewMode} />
                      <MetaRow label="Body Part" value={study.body_part} />
                      <MetaRow label="Date" value={study.study_date} />
                      <MetaRow label="Status" value={study.status} />
                      {study.findings && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Findings</p>
                          <p className="text-xs leading-relaxed text-muted-foreground">{study.findings}</p>
                        </div>
                      )}
                      <Separator />
                    </>
                  )}

                  {selectedFile?.file_type === "video" && selectedFile?.avi_meta && (
                    <>
                      <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Video</h4>
                      <MetaRow label="FPS" value={String(selectedFile.avi_meta.fps ?? "N/A")} />
                      <MetaRow label="Duration" value={selectedFile.avi_meta.duration != null ? `${selectedFile.avi_meta.duration.toFixed(1)}s` : "N/A"} />
                      <MetaRow label="Frames" value={String(selectedFile.avi_meta.frame_count ?? "N/A")} />
                      <Separator />
                    </>
                  )}

                  {isMultiFile && seriesSlices.length > 0 && (
                    <>
                      <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Series</h4>
                      <MetaRow label="Type" value="Multi-file series" />
                      <MetaRow label="Total slices" value={String(seriesSlices.length)} />
                      <MetaRow label="Current" value={String(sliceIdx + 1)} />
                      {seriesSlices[sliceIdx]?.pos_z != null && (
                        <MetaRow label="Position Z" value={seriesSlices[sliceIdx].pos_z.toFixed(2)} />
                      )}
                      <Separator />
                    </>
                  )}

                  {loadingMeta ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : serverMetadata ? (
                    <>
                      <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">DICOM Metadata</h4>
                      {Object.entries(serverMetadata).map(([key, val]) => (
                        <MetaRow key={key} label={key} value={Array.isArray(val) ? val.join(", ") : String(val)} />
                      ))}
                    </>
                  ) : selectedFile ? (
                    <>
                      <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">File</h4>
                      <MetaRow label="Filename" value={selectedFile.original_filename} />
                      <MetaRow label="Type" value={selectedFile.file_type || "N/A"} />
                      <MetaRow label="Modality" value={getModality(selectedFile.modality)?.label ?? selectedFile.modality ?? "N/A"} />
                      <MetaRow label="Patient" value={selectedFile.patient_name || "N/A"} />
                      <MetaRow label="Frames" value={String(selectedFile.num_frames || 1)} />
                      <MetaRow label="Size" value={formatBytes(selectedFile.file_size)} />
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-8">
                      Select a file to view info
                    </p>
                  )}
                </div>
              </ScrollArea>
            </TabsContent> */}

            <TabsContent value="discussion" className="flex-1 overflow-hidden m-0">
              {discussionStudyId ? (
                <StudyDiscussion studyId={discussionStudyId} compact />
              ) : (
                <div className="flex flex-col items-center justify-center h-full px-4">
                  <MessageSquare className="w-7 h-7 text-muted-foreground/30 mb-2" />
                  <p className="text-xs text-muted-foreground text-center">
                    Navigate from a study to enable discussion
                  </p>
                </div>
              )}
            </TabsContent>
            </Tabs>
          </Card>
        </div>
      ) : !isShare ? (
        <div className="flex-shrink-0 self-start" data-testid="viewer-discussion-collapsed">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-lg"
            onClick={() => setDiscussionOpen(true)}
            aria-label="Expand discussion"
            data-testid="expand-discussion"
          >
            <MessageSquare className="w-4 h-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-sm ${mono ? "font-mono text-primary" : ""}`}>{value || "N/A"}</p>
    </div>
  );
}

const isDicomdirFilename = (name) => /dicomdir/i.test(String(name || ""));

function FluoroFilePreview({
  file, order = 0, active, viewerReady, selected, onSelect, onToggleSelect,
  shareToken = null, hideSelect = false,
}) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const label = file?.original_filename || file?.filename || "File";
  const isMultiFile = file?.series_type === "multi_file";
  const frameHint = isMultiFile
    ? (file?.slice_count ? `${file.slice_count}s` : null)
    : (file?.num_frames > 1 ? `${file.num_frames}f` : null);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { rootMargin: "220px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!file?.id || isDicomdirFilename(label)) {
      setFailed(true);
      return undefined;
    }
    if (
      !isMultiFile
      && file?.file_type
      && file.file_type !== "dicom"
      && !file?.is_sample
    ) {
      setFailed(true);
      return undefined;
    }
    if (!active && !isVisible) return undefined;

    let cancelled = false;
    let objectUrl = null;
    let timer = null;

    const applyBlob = (blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setThumbUrl(objectUrl);
    };

    if (isMultiFile) {
      if (shareToken) {
        setFailed(true);
        return undefined;
      }
      setThumbUrl(null);
      setFailed(false);
      const staggerMs = active ? 0 : Math.min(1800, 300 + order * 120);
      timer = window.setTimeout(() => {
        axios.get(`${API}/dicom/series/${file.id}/slice/0/image`, { responseType: "blob" })
          .then((res) => {
            applyBlob(res.data);
            if (!active) {
              prefetchFluoroRawSlice0(file.id, { urgent: false }).catch(() => {});
            }
          })
          .catch(() => {
            if (!cancelled) setFailed(true);
          });
      }, staggerMs);
      return () => {
        cancelled = true;
        if (timer) window.clearTimeout(timer);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    if (active) {
      const cached = getCachedDicomFilePreviewThumb(file.id);
      if (cached) {
        setThumbUrl(cached);
        setFailed(false);
      }
      if (!viewerReady) return undefined;
      let tries = 0;
      const tryCapture = () => {
        if (cancelled) return;
        if (getCachedDicomFilePreviewThumb(file.id)) return;
        const captured = captureDwvLayerThumb();
        if (captured) {
          rememberDicomFilePreviewThumb(file.id, captured);
          setThumbUrl(captured);
          setFailed(false);
          return;
        }
        tries += 1;
        if (tries < 12) {
          timer = window.setTimeout(tryCapture, 250);
        }
      };
      timer = window.setTimeout(tryCapture, 50);
      return () => {
        cancelled = true;
        if (timer) window.clearTimeout(timer);
      };
    }

    const cached = getCachedDicomFilePreviewThumb(file.id);
    if (cached) {
      setThumbUrl(cached);
      setFailed(false);
      return undefined;
    }

    setFailed(false);
    if (shareToken) {
      setFailed(true);
      return undefined;
    }
    const staggerMs = Math.min(1800, 300 + order * 120);
    timer = window.setTimeout(() => {
      getDicomFilePreviewThumb(file.id)
        .then((dataUrl) => {
          if (cancelled || !dataUrl) return;
          setThumbUrl(dataUrl);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }, staggerMs);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [file?.id, isMultiFile, active, isVisible, order, label, viewerReady, shareToken]);

  return (
    <div>
      <button
        ref={cardRef}
        type="button"
        data-testid={`viewer-file-${file?.id}`}
        onClick={onSelect}
        className={`w-full text-left border rounded-md overflow-hidden transition-colors ${
          active
            ? "border-primary bg-primary/10 ring-1 ring-primary/40"
            : "border-border/70 hover:bg-secondary/40"
        }`}
        aria-label={`Play ${label}`}
        aria-current={active ? "true" : undefined}
      >
        <div className="relative w-full h-32 bg-black flex items-center justify-center">
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={`${label} preview`}
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center gap-1 px-2">
              {failed ? (
                <MonitorPlay className="w-5 h-5 text-zinc-500" />
              ) : (
                <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
              )}
              <span className="text-[10px] text-zinc-400 text-center line-clamp-2">{label}</span>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
            <p className="text-[10px] text-white truncate font-medium">{label}</p>
            {frameHint && (
              <p className="text-[9px] text-zinc-300 tabular-nums">{frameHint}</p>
            )}
          </div>
        </div>
      </button>
      {!hideSelect && (
      <label className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer mt-1">
        <input
          type="checkbox"
          checked={Boolean(selected)}
          onChange={onToggleSelect}
          aria-label={`Select ${label}`}
        />
        Select
      </label>
      )}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return "N/A";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}
